import React, { useEffect, useMemo, useRef, useState } from "react";
import { BellIcon, XIcon, PackageIcon, RouteIcon, CheckCircle2Icon } from "lucide-react";
import {
  fetchNotifications,
  filterTodayNotifications,
  countUnreadNotifications,
  markNotificationRead,
  type DeliveryNotificationItem,
} from "../services/notification.service";

interface NotificationBellProps {
  onOpenTab: (tab: "basket" | "today" | "route" | "completed") => void;
}

function iconForType(type: string) {
  const t = type.toLowerCase();
  if (t.includes("delivered") || t.includes("completed")) {
    return <CheckCircle2Icon size={16} className="text-emerald-600 mt-0.5 shrink-0" />;
  }
  if (t.includes("route")) {
    return <RouteIcon size={16} className="text-blue-600 mt-0.5 shrink-0" />;
  }
  return <PackageIcon size={16} className="text-orange-600 mt-0.5 shrink-0" />;
}

function tabForNotification(n: DeliveryNotificationItem): "basket" | "today" | "route" | "completed" {
  const t = (n.notification_type || "").toLowerCase();
  if (t.includes("route")) return "route";
  if (t.includes("delivered") || t.includes("completed")) return "completed";
  return "today";
}

function timeAgo(iso: string): string {
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    if (ms < 60_000) return "Just now";
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onOpenTab }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [rows, setRows] = useState<DeliveryNotificationItem[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  const unreadRows = useMemo(() => rows.filter((r) => !r.is_read), [rows]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const list = filterTodayNotifications(await fetchNotifications(false));
      setRows(list);
      setUnreadCount(countUnreadNotifications(list));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    void loadAll();
  }, [open]);

  useEffect(() => {
    const refreshBadge = async () => {
      try {
        const list = filterTodayNotifications(await fetchNotifications(false));
        setUnreadCount(countUnreadNotifications(list));
        if (open) setRows(list);
      } catch {
        // no-op
      }
    };
    void refreshBadge();
    const interval = window.setInterval(() => void refreshBadge(), 30_000);
    return () => window.clearInterval(interval);
  }, [open]);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const onItemClick = async (item: DeliveryNotificationItem) => {
    if (!item.is_read) {
      const ok = await markNotificationRead(item.id);
      if (ok) {
        setRows((prev) => prev.map((r) => (r.id === item.id ? { ...r, is_read: true } : r)));
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    }
    onOpenTab(tabForNotification(item));
    setOpen(false);
  };

  const onMarkAllRead = async () => {
    const unreadToday = unreadRows;
    if (unreadToday.length === 0) return;
    const results = await Promise.all(
      unreadToday.map((n) => markNotificationRead(n.id))
    );
    if (!results.every(Boolean)) return;
    setRows((prev) => prev.map((r) => ({ ...r, is_read: true })));
    setUnreadCount(0);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 bg-orange-600 rounded-full hover:bg-orange-600/90"
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ""}`}
      >
        <BellIcon size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] font-semibold text-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-h-[420px] bg-white text-gray-800 rounded-xl shadow-xl border border-orange-100 z-50 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm">Notifications</p>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? `${unreadCount} unread today` : "All caught up today"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {unreadRows.length > 0 && (
                <button onClick={onMarkAllRead} className="text-xs text-orange-600 font-medium">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-gray-100">
                <XIcon size={16} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto">
            {loading ? (
              <div className="p-4 text-sm text-gray-500">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-gray-500 text-center">
                No notifications for today
              </div>
            ) : (
              rows.map((n) => (
                <button
                  key={n.id}
                  onClick={() => void onItemClick(n)}
                  className={`w-full text-left px-3 py-3 border-b border-gray-50 hover:bg-gray-50 ${
                    n.is_read ? "bg-white" : "bg-orange-50/40"
                  }`}
                >
                  <div className="flex gap-2">
                    {iconForType(n.notification_type)}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{n.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
